import React, {useEffect, useState} from "react";
import localforage from "localforage";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import Swal from "sweetalert2";

interface Stakeholder {
    id: number;
    name: string;
    category: string;
    location: string;
    services: string[];
    description: string;
    contact_phone: string;
    contact_email: string;
    request_count: number;
}

const ResearchPartners: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [pageTitle] = useState("Research & Partners")
    const [clinicName, setClinicName] = useState<any>("Facility")
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    const [partners, setPartners] = useState<Stakeholder[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: '', category: '', location: '', description: '', contact_phone: '', contact_email: '', services: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(()=>{
        document.title = "Uzima - Research & Partners"
        localforage.getItem("clinic_name").then((clini)=>{
            setClinicName(clini)
        })
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, [])

    const loadPartners = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholders/?clinic_id=${clinicId}&type=research`));
            const data = await res.json();
            if (data.status === 'success') setPartners(data.data || []);
        } catch { /* leave list as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPartners(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitPartner = async () => {
        if (!form.name.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Name is required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholders/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clinic_id: clinicId, staff_id: staffId, type: 'research', ...form,
                    services: form.services.split(',').map(s => s.trim()).filter(Boolean),
                }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Research Partner Added', timer: 1500, showConfirmButton: false });
                setShowAdd(false);
                setForm({ name: '', category: '', location: '', description: '', contact_phone: '', contact_email: '', services: '' });
                loadPartners();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to add partner.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while adding partner.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <DashboardHeader></DashboardHeader>
            <SideMenu></SideMenu>

            <div className={"vertical-overlay"}></div>

            <div className={"main-content"}>
                <div className={"page-content"}>

                    <div className={"container-fluid"}>

                        <div className="row">
                            <div className="col-12">
                                <div
                                    className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0  ">{clinicName} - {pageTitle} &nbsp;
                                    </h4>

                                    <div className="page-title-right d-flex align-items-center gap-3">
                                        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
                                            <i className="ri-add-line me-1"></i>Add Research Partner
                                        </button>
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a
                                                href="/institution/home">Dashboards</a></li>
                                            <li className="breadcrumb-item active">RND</li>
                                        </ol>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                            </div>
                        ) : partners.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="ri-microscope-line" style={{ fontSize: '2.5rem' }}></i>
                                <h5 className="mt-3">No research partners on file</h5>
                                <p className="text-muted">Add research institutions or organizations your facility collaborates with
                                    on clinical research, studies, or trials.</p>
                                <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
                                    <i className="ri-add-line me-1"></i>Add Your First Research Partner
                                </button>
                            </div>
                        ) : (
                            <div className="row g-4">
                                {partners.map(partner => (
                                    <div key={partner.id} className="col-lg-6">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h5 className="card-title mb-1">{partner.name}</h5>
                                                    <span className="badge bg-light text-dark">{partner.request_count} request{partner.request_count === 1 ? '' : 's'}</span>
                                                </div>
                                                {partner.category && <p className="text-muted mb-2">{partner.category}</p>}
                                                {partner.location && (
                                                    <div className="d-flex align-items-center text-muted mb-2">
                                                        <i className="ri-map-pin-line me-1"></i>
                                                        <small>{partner.location}</small>
                                                    </div>
                                                )}
                                                {partner.description && <p className="card-text text-muted mb-3">{partner.description}</p>}
                                                {partner.services.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mb-2">
                                                        {partner.services.map((s, i) => <span key={i} className="badge bg-light text-dark">{s}</span>)}
                                                    </div>
                                                )}
                                                {partner.contact_phone && (
                                                    <div><i className="ri-phone-line me-1"></i><small className="text-muted">{partner.contact_phone}</small></div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>

                </div>

                <DashboardFooter></DashboardFooter>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Research Partner</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input type="text" className="form-control" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Research Focus / Category</label>
                                    <input type="text" className="form-control" placeholder="e.g. Infectious Disease Research" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Location</label>
                                    <input type="text" className="form-control" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Services (comma-separated)</label>
                                    <input type="text" className="form-control" placeholder="e.g. Clinical Trials, Disease Surveillance" value={form.services} onChange={(e) => setForm(f => ({ ...f, services: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Contact Phone</label>
                                        <input type="text" className="form-control" value={form.contact_phone} onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Contact Email</label>
                                        <input type="email" className="form-control" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitPartner}>{submitting ? 'Saving...' : 'Add Partner'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ResearchPartners
